// Hands a Custom GL Mapping Rule off from the AI chat to the real Add Rule
// modal in Settings → Configurations.
//
// Same shape and reasoning as pendingChatImport.ts: the chat panel lives
// outside the Settings drawer, so the request is stashed here and a DOM event
// wakes whichever component is mounted. The same-tick "already pending" read
// covers the drawer mounting fresh via navigation after the event has fired.
//
// Why a handoff rather than a form in the chat: building a mapping rule means
// criteria (employees, customers, classes, operators, the Any/Blank
// sentinels) plus a debit/credit account per cost field. That is the Add Rule
// modal's job, and duplicating it in a chat column produces a worse version of
// a screen that already exists. The chat collects the two free-text fields
// nobody needs a picker for and opens the real thing on the rest.

export interface PendingChatMappingRule {
	// Prefill for a NEW rule. Passed to the modal as modalData with
	// isCopy: true, which is the existing "prefill but still POST" path — plain
	// modalData would put the modal in edit mode and PUT to a nonexistent id.
	name?: string;
	description?: string;
	// Set instead when the chat resolved an existing rule to edit.
	ruleId?: string;
}

export const CHAT_MAPPING_RULE_EVENT = 'cap:chat-mapping-rule-request';

let pending: PendingChatMappingRule | null = null;

export function requestChatMappingRule(request: PendingChatMappingRule) {
	pending = request;
	window.dispatchEvent(new CustomEvent(CHAT_MAPPING_RULE_EVENT));
}

// Reads the pending request WITHOUT clearing it. Two separate components need
// to see it: Configurations has to switch to the Custom Mapping tab (key '4')
// before the rules table even mounts, and the table then opens the modal. If
// the first of those consumed it, the second would never see it.
//
// It also lets the table wait: an edit request needs the rules list loaded to
// find its rule, and its effect runs once on mount while that list is still
// empty. Peeking there and only consuming once the rule is actually found
// stops that first empty pass from throwing the request away.
export function peekChatMappingRule(): PendingChatMappingRule | null {
	return pending;
}

export function consumeChatMappingRule(): PendingChatMappingRule | null {
	if (!pending) {
		return null;
	}
	const request = pending;
	pending = null;
	return request;
}
